import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import Breadcrumbs from '../components/Breadcrumbs'

const LEGAL_TYPES = ['mentions-legales', 'rgpd', 'cookies'] as const
type LegalType = (typeof LEGAL_TYPES)[number]

function getLegalType(pathname: string): LegalType {
  if (pathname === '/rgpd') return 'rgpd'
  if (pathname === '/cookies') return 'cookies'
  return 'mentions-legales'
}

const LEGAL_BREADCRUMB_KEYS: Record<LegalType, string> = {
  'mentions-legales': 'mentionsLegales',
  'rgpd': 'rgpd',
  'cookies': 'cookies',
}

export default function LegalPage() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const legalType = getLegalType(pathname)

  const titleKey = `legal.${legalType}.title`
  const contentKey = `legal.${legalType}.content`

  return (
    <div className="pb-20 min-h-screen">
      <Breadcrumbs items={[{ key: LEGAL_BREADCRUMB_KEYS[legalType] }]} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline mb-8"
          >
            <span className="material-icons text-lg">arrow_back</span>
            {t('legal.back')}
          </Link>

          <h1 className="text-3xl sm:text-4xl font-bold mb-8">
            {t(titleKey)}
          </h1>

          <div className="prose prose-slate dark:prose-invert max-w-none text-slate-600 dark:text-slate-400 space-y-6">
            {(() => {
              const content = t(contentKey, { returnObjects: true })
              const paragraphs = Array.isArray(content) ? content : [content]
              return paragraphs.map((paragraph, i) => (
                <p key={i} className="leading-relaxed">
                  {paragraph}
                </p>
              ))
            })()}
          </div>

          <div className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('legal.lastUpdated')} : 2026
            </p>
            <Link
              to="/contact"
              className="inline-block mt-4 text-primary font-medium hover:underline"
            >
              {t('footer.contact')} — NiceDay Technologies
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
